import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { posService } from '../../../lib/posService';

const LocationAnalytics = ({ locationId, locationName, products }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');

  useEffect(() => {
    loadTransactions();
  }, [locationId, dateRange]);

  const loadTransactions = async () => {
    setLoading(true);
    const filters = getDateFilters();
    const { data } = await posService?.getTransactions(locationId, filters);
    if (data) {
      setTransactions(data);
    }
    setLoading(false);
  };

  const getDateFilters = () => {
    const now = new Date();
    const today = new Date(now?.getFullYear(), now?.getMonth(), now?.getDate());
    
    switch (dateRange) {
      case 'today':
        return { startDate: today?.toISOString() };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo?.setDate(weekAgo?.getDate() - 7);
        return { startDate: weekAgo?.toISOString() };
      case 'month':
        const monthAgo = new Date(today);
        monthAgo?.setMonth(monthAgo?.getMonth() - 1);
        return { startDate: monthAgo?.toISOString() };
      default:
        return {};
    }
  };

  const totalRevenue = transactions?.reduce((sum, t) => sum + parseFloat(t?.total || 0), 0);
  const totalTransactions = transactions?.length;
  const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  const inventoryValue = products?.reduce((sum, p) => sum + (p?.price * p?.stock_quantity), 0);
  const lowStockCount = products?.filter(p => p?.stock_quantity <= p?.low_stock_threshold)?.length;
  const outOfStockCount = products?.filter(p => p?.stock_quantity === 0)?.length;

  const paymentMethodBreakdown = transactions?.reduce((acc, t) => {
    acc[t?.payment_method] = (acc?.[t?.payment_method] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="card">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Analytics - {locationName}</h2>
          <div className="flex gap-2">
            {['today', 'week', 'month']?.map(range => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === range
                    ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {range?.charAt(0)?.toUpperCase() + range?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Revenue Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card border-l-4 border-l-success">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="DollarSign" size={24} color="var(--color-success)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold text-foreground">${totalRevenue?.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="card border-l-4 border-l-primary">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="ShoppingCart" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-2xl font-bold text-foreground">{totalTransactions}</p>
                </div>
              </div>
            </div>

            <div className="card border-l-4 border-l-accent">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg Transaction</p>
                  <p className="text-2xl font-bold text-foreground">${averageTransaction?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Inventory Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="flex items-center gap-3">
                <Icon name="Package" size={20} color="var(--color-primary)" />
                <div>
                  <p className="text-xs text-muted-foreground">Total Products</p>
                  <p className="text-xl font-bold text-foreground">{products?.length}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
                <div>
                  <p className="text-xs text-muted-foreground">Inventory Value</p>
                  <p className="text-xl font-bold text-foreground">${inventoryValue?.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <div>
                  <p className="text-xs text-muted-foreground">Low Stock</p>
                  <p className="text-xl font-bold text-warning">{lowStockCount}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <Icon name="XCircle" size={20} color="var(--color-error)" />
                <div>
                  <p className="text-xs text-muted-foreground">Out of Stock</p>
                  <p className="text-xl font-bold text-error">{outOfStockCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="card">
            <h3 className="text-lg font-semibold text-foreground mb-4">Payment Methods</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(paymentMethodBreakdown)?.map(([method, count]) => (
                <div key={method} className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground capitalize">{method?.replace('_', ' ')}</p>
                      <p className="text-2xl font-bold text-foreground">{count}</p>
                    </div>
                    <Icon
                      name={method === 'cash' ? 'Banknote' : method === 'credit_card' ? 'CreditCard' : 'User'}
                      size={32}
                      color="var(--color-primary)"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="card">
            <h3 className="text-lg font-semibold text-foreground mb-4">Recent Transactions</h3>
            {transactions?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Receipt" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-muted-foreground">No transactions in selected period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Transaction #</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Staff</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Payment</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Total</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions?.slice(0, 10)?.map(transaction => (
                      <tr key={transaction?.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm font-medium text-foreground">
                          {transaction?.transaction_number}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {transaction?.staff?.full_name || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground capitalize">
                          {transaction?.payment_method?.replace('_', ' ')}
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-foreground text-right">
                          ${parseFloat(transaction?.total)?.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {new Date(transaction?.created_at)?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LocationAnalytics;